import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { useDismiss } from "@/shared/hooks/useDismiss";
import {
  canonicalFieldLabel,
  type ImportableDimension
} from "@/shared/vocabulary/canonical-fields";
import { toggleDimension } from "./imported-dimensions";

/**
 * "Configurações avançadas" do envio (2026-10-07): de onde vem a
 * classificação de cada dimensão — do Reputaition (padrão) ou da coluna
 * da planilha.
 *
 * Só oferece as dimensões que o template do arquivo mapeia; o pai já
 * filtra. As escolhas valem na hora (não há "cancelar": fechar só
 * esconde, e o botão da linha do arquivo mostra a bolinha vermelha
 * enquanto houver algo fora do padrão).
 */

const HINTS: Record<ImportableDimension, string> = {
  polarity: "O texto da célula tem que ser uma polaridade da escala da empresa.",
  approach: "Protagonista, Coadjuvante ou Citação.",
  themes: "Vários subtemas na mesma célula, separados por ponto e vírgula (;).",
  key_messages: "Várias mensagens na mesma célula, separadas por ponto e vírgula (;)."
};

export function ImportedClassificationDialog({
  fileName,
  available,
  selected,
  onChange,
  onClose
}: {
  fileName: string;
  available: readonly ImportableDimension[];
  selected: readonly ImportableDimension[];
  onChange: (next: ImportableDimension[]) => void;
  onClose: () => void;
}) {
  useDismiss(true, onClose);

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container imported-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="imported-dialog-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">CONFIGURAÇÕES AVANÇADAS</span>
            <h2 id="imported-dialog-title">Classificação da planilha</h2>
            <p className="imported-dialog-target">{fileName}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">
          <p className="imported-dialog-lead">
            Por padrão, tudo é classificado pelo Reputaition (regras e IA). Marque o que você
            quer tomar da coluna da planilha — essas dimensões não passam pela IA.
          </p>
          <div className="imported-dialog-list">
            {available.map(dimension => (
              <label key={dimension} className="imported-dialog-row">
                <input
                  type="checkbox"
                  checked={selected.includes(dimension)}
                  onChange={event =>
                    onChange(toggleDimension(selected, dimension, event.target.checked))
                  }
                />
                <span>
                  <strong>Usar {canonicalFieldLabel(dimension).toLowerCase()} da planilha</strong>
                  <small>{HINTS[dimension]}</small>
                </span>
              </label>
            ))}
          </div>
          <p className="imported-dialog-note">
            O texto tem que vir como o Reputaition escreve. Valor que não casar com o cadastro
            vai para a fila de correção do upload, onde você cria a entrada ou escolhe uma que já
            existe.
          </p>
          <div className="imported-dialog-actions">
            <button className="primary-button" type="button" onClick={onClose}>
              Concluído
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
